import { 
  themeQuartz, 
  iconSetQuartzLight, //icons
} from 'ag-grid-community';

/**
 * Mojave theme for AG Grid (Light Mode)
 * 
 * This theme uses AG Grid's Theming API to apply Mojave design tokens.
 * All values reference CSS custom properties defined in aggrid-semantic-mojave.css / aggrid-semantic-biome.css
 * which must be imported in your application.
 * 
 * @example
 * 
 * import { agGridMojaveLight, agGridMojaveDark } from './aggrid-theme';
 * 
 * <AgGridReact theme={agGridMojaveLight} />
 * 
 */
const sharedParams = {
  // ============================================
  // COLORS - BACKGROUNDS
  // ============================================
  backgroundColor: 'var(--ag-grid-background-color)',
  foregroundColor: 'var(--ag-grid-foreground-color)',
  chromeBackgroundColor: 'var(--ag-grid-chrome-background-color)',
  oddRowBackgroundColor: 'var(--ag-grid-odd-row-background-color)',
  modalOverlayBackgroundColor: 'var(--ag-grid-modal-overlay-background-color)',

  // ============================================
  // COLORS - INTERACTIVE STATES
  // ============================================
  selectedRowBackgroundColor: 'var(--ag-grid-selected-row-background-color)',
  rangeSelectionBackgroundColor: 'var(--ag-grid-range-selection-background-color)',
  rangeSelectionHighlightColor: 'var(--ag-grid-range-selection-highlight-color)',
  rangeSelectionChartBackgroundColor: 'var(--ag-grid-range-selection-chart-background-color)',
  rangeSelectionChartCategoryBackgroundColor: 'var(--ag-grid-range-selection-chart-category-background-color)',
  rowHoverColor: 'var(--ag-grid-row-hover-color)',
  columnHoverColor: 'var(--ag-grid-column-hover-color)',
  headerCellHoverBackgroundColor: 'var(--ag-grid-header-cell-hover-background-color)',
  headerCellMovingBackgroundColor: 'var(--ag-grid-header-cell-moving-background-color)',

  // ============================================
  // COLORS - ACCENT & VALIDATION
  // ============================================
  accentColor: 'var(--ag-grid-accent-color)',
  invalidColor: 'var(--ag-grid-invalid-color)',
  valueChangeDeltaUpColor: 'var(--ag-grid-value-change-delta-up-color)',
  valueChangeDeltaDownColor: 'var(--ag-grid-value-change-delta-down-color)',
  valueChangeValueHighlightBackgroundColor: 'var(--ag-grid-value-change-value-highlight-background-color)',

  // ============================================
  // HEADER
  // ============================================
  headerBackgroundColor: 'var(--ag-grid-header-background-color)',
  headerTextColor: 'var(--ag-grid-foreground-color)',
  headerFontFamily: 'var(--ag-grid-header-font-family)',
  headerFontSize: 'var(--ag-grid-header-font-size)',
  headerFontWeight: 'var(--ag-grid-header-font-weight)',
  headerHeight: 'var(--ag-grid-header-height)',
  headerVerticalPaddingScale: 1, //This cannot be defined with a variable because it doesn't accept strings
  headerCellBackgroundTransitionDuration: 'var(--ag-grid-header-cell-background-transition-duration)',
  headerColumnBorder: false, // Set to true if you want vertical lines between header cells
  headerColumnBorderHeight: 'var(--ag-grid-header-column-border-height)',
  headerColumnResizeHandleHeight: 'var(--ag-grid-header-column-resize-handle-height)',
  headerColumnResizeHandleWidth: 'var(--ag-grid-header-column-resize-handle-width)',

  // ============================================
  // ROWS
  // ============================================
  rowHeight: 'var(--ag-grid-row-height)',
  rowVerticalPaddingScale: 1, //This cannot be defined with a variable because it doesn't accept strings
  rowBorder: true,
  headerRowBorder: true,
  footerRowBorder: true,
  rowLoadingSkeletonEffectColor: 'var(--ag-grid-row-loading-skeleton-effect-color)',
  rowNumbersSelectedColor: 'var(--ag-grid-row-numbers-selected-color)',
  rowGroupIndentSize: 'var(--ag-grid-row-group-indent-size)',
  //rowDragIndicatorWidth: 'var(--ag-grid-row-drag-indicator-width)',

  // ============================================
  // CELLS
  // ============================================
  cellFontFamily: 'var(--ag-grid-cell-font-family)',
  cellTextColor: 'var(--ag-grid-cell-text-color)',
  cellHorizontalPadding: 'var(--ag-grid-cell-horizontal-padding)',
  cellHorizontalPaddingScale: 1, //This cannot be defined with a variable because it doesn't accept strings
  cellWidgetSpacing: 'var(--ag-grid-cell-widget-spacing)',
  //cellBatchEditBackgroundColor: 'var(--ag-grid-cell-batch-edit-background-color)',
  //cellBatchEditTextColor: 'var(--ag-grid-cell-batch-edit-text-color)',
  cellEditingBorder: true,
  selectCellBorder: true,

  // ============================================
  // BORDERS & RADIUS
  // ============================================
  borderColor: 'var(--ag-grid-border-color)',
  //borderWidth: 'var(--ag-grid-border-width)',
  borderRadius: 'var(--ag-grid-border-radius)',
  wrapperBorder: true,
  wrapperBorderRadius: 'var(--ag-grid-wrapper-border-radius)',
  columnBorder: false, // Transparent by design
  pinnedColumnBorder: true,
  pinnedRowBorder: true,
  sidePanelBorder: true,
  rangeSelectionBorderStyle: 'var(--ag-grid-range-selection-border-style)',

  // ============================================
  // TYPOGRAPHY
  // ============================================
  fontFamily: 'var(--ag-grid-font-family)',
  fontSize: 'var(--ag-grid-font-size)',
  dataFontSize: 'var(--ag-grid-data-font-size)',

  // ============================================
  // SPACING
  // ============================================
  spacing: 'var(--ag-grid-size)',

  // ============================================
  // BUTTONS
  // ============================================
  buttonTextColor: 'var(--ag-grid-button-text-color)',
  buttonBackgroundColor: 'var(--ag-grid-button-background-color)',
  buttonBorder: true,
  buttonBorderRadius: 'var(--ag-grid-button-border-radius)',
  buttonFontWeight: 'var(--ag-grid-button-font-weight)',
  buttonHorizontalPadding: 'var(--ag-grid-button-horizontal-padding)',
  buttonVerticalPadding: 'var(--ag-grid-button-vertical-padding)',
  buttonHoverTextColor: 'var(--ag-grid-button-hover-text-color)',
  buttonHoverBackgroundColor: 'var(--ag-grid-button-hover-background-color)',
  buttonHoverBorder: true,
  buttonActiveBackgroundColor: 'var(--ag-grid-button-active-background-color)',
  buttonActiveBorder: true,
  buttonDisabledTextColor: 'var(--ag-grid-button-disabled-text-color)',
  buttonDisabledBackgroundColor: 'var(--ag-grid-button-disabled-background-color)',
  buttonDisabledBorder: true,

  // ============================================
  // CHECKBOXES & TOGGLES
  // ============================================
  checkboxBorderRadius: 'var(--ag-grid-checkbox-border-radius)',
  checkboxBorderWidth: 'var(--ag-grid-checkbox-border-width)',
  checkboxCheckedBackgroundColor: 'var(--ag-grid-checkbox-checked-color)',
  checkboxUncheckedBorderColor: 'var(--ag-grid-checkbox-unchecked-border-color)',
  checkboxIndeterminateBackgroundColor: 'var(--ag-grid-checkbox-indeterminate-background-color)',
  toggleButtonWidth: 'var(--ag-grid-toggle-button-width)',
  toggleButtonHeight: 'var(--ag-grid-toggle-button-height)',
  toggleButtonSwitchInset: 'var(--ag-grid-toggle-button-switch-inset)',

  // ============================================
  // ICONS
  // ============================================
  iconSize: 'var(--ag-grid-icon-size)',
  iconColor: 'var(--ag-grid-icon-color)',
  iconButtonColor: 'var(--ag-grid-icon-button-color)',
  iconButtonBackgroundColor: 'var(--ag-grid-icon-button-background-color)',
  iconButtonBackgroundSpread: 'var(--ag-grid-icon-button-background-spread)',
  iconButtonBorderRadius: 'var(--ag-grid-icon-button-border-radius)',
  iconButtonHoverBackgroundColor: 'var(--ag-grid-icon-button-hover-background-color)',
  iconButtonActiveBackgroundColor: 'var(--ag-grid-icon-button-active-background-color)',

  // ============================================
  // MENUS & DROPDOWNS
  // ============================================
  menuBackgroundColor: 'var(--ag-grid-menu-background-color)',
  menuBorder: true,
  menuShadow: 'var(--ag-grid-menu-shadow)',
  listItemHeight: 'var(--ag-grid-list-item-height)',
  pickerButtonBorder: true,
  pickerButtonFocusBorder: true,
  pickerListBorder: true,

  // ============================================
  // INPUTS
  // ============================================
  inputBorder: true,
  inputBorderRadius: 'var(--ag-grid-input-border-radius)',
  inputHeight: 'var(--ag-grid-input-height)',
  inputPaddingStart: 'var(--ag-grid-input-padding-start)',
  inputPlaceholderTextColor: 'var(--ag-grid-input-placeholder-text-color)',
  inputFocusBorder: true,
  inputFocusShadow: 'var(--ag-grid-input-focus-box-shadow)',
  inputDisabledBorder: true,
  inputDisabledTextColor: 'var(--ag-grid-input-disabled-text-color)',
  inputInvalidBorder: true,
  inputBackgroundColor: 'var(--ag-grid-background-color)',

  // ============================================
  // PANELS & SIDEBARS
  // ============================================
  sideBarPanelWidth: 'var(--ag-grid-side-bar-panel-width)',
  sideButtonBackgroundColor: 'var(--ag-grid-side-button-background-color)',
  sideButtonBarTopPadding: 'var(--ag-grid-side-button-bar-top-padding)',
  sideButtonBorder: false,
  sideButtonLeftPadding: 'var(--ag-grid-side-button-left-padding)',
  sideButtonRightPadding: 'var(--ag-grid-side-button-right-padding)',
  sideButtonVerticalPadding: 'var(--ag-grid-side-button-vertical-padding)',
  sideButtonSelectedBorder: true,
  sideButtonSelectedUnderlineColor: 'var(--ag-grid-side-button-selected-underline-color)',
  sideButtonSelectedUnderlineWidth: 'var(--ag-grid-side-button-selected-underline-width)',
  sideButtonSelectedUnderlineTransitionDuration: 'var(--ag-grid-side-button-selected-underline-transition-duration)',
  panelTitleBarBorder: true,
  //panelTitleBarHeight: 'var(--ag-grid-panel-title-bar-height)',
  //panelTitleBarFontFamily: 'var(--ag-grid-panel-title-bar-font-family)',
  //panelTitleBarFontSize: 'var(--ag-grid-panel-title-bar-font-size)',
  panelTitleBarFontWeight: 'var(--ag-grid-panel-title-bar-font-weight)',
  toolPanelSeparatorBorder: true,

  // ============================================
  // TABS
  // ============================================
  tabBarBackgroundColor: 'var(--ag-grid-tab-bar-background-color)',
  tabBarBorder: true,
  tabBarHorizontalPadding: 'var(--ag-grid-tab-bar-horizontal-padding)',
  tabBarTopPadding: 'var(--ag-grid-tab-bar-top-padding)',
  tabBackgroundColor: 'var(--ag-grid-tab-background-color)',
  tabHorizontalPadding: 'var(--ag-grid-tab-horizontal-padding)',
  tabTopPadding: 'var(--ag-grid-tab-top-padding)',
  tabBottomPadding: 'var(--ag-grid-tab-bottom-padding)',
  tabSpacing: 'var(--ag-grid-tab-spacing)',
  tabSelectedBorderWidth: 'var(--ag-grid-tab-selected-border-width)',
  tabSelectedUnderlineColor: 'var(--ag-grid-tab-selected-underline-color)',
  tabSelectedUnderlineWidth: 'var(--ag-grid-tab-selected-underline-width)',
  tabSelectedUnderlineTransitionDuration: 'var(--ag-grid-tab-selected-underline-transition-duration)',

  // ============================================
  // ADVANCED FILTER BUILDER
  // ============================================
  advancedFilterBuilderButtonBarBorder: true,
  advancedFilterBuilderIndentSize: 'var(--ag-grid-advanced-filter-builder-indent-size)',
  advancedFilterBuilderJoinPillColor: 'var(--ag-grid-advanced-filter-builder-join-pill-color)',
  advancedFilterBuilderColumnPillColor: 'var(--ag-grid-advanced-filter-builder-column-pill-color)',
  advancedFilterBuilderOptionPillColor: 'var(--ag-grid-advanced-filter-builder-option-pill-color)',
  advancedFilterBuilderValuePillColor: 'var(--ag-grid-advanced-filter-builder-value-pill-color)',

  // ============================================
  // COLUMN DROP & DRAG
  // ============================================
  columnDropCellBackgroundColor: 'var(--ag-grid-column-drop-cell-background-color)',
  columnDropCellTextColor: 'var(--ag-grid-column-drop-cell-text-color)',
  columnDropCellDragHandleColor: 'var(--ag-grid-column-drop-cell-drag-handle-color)',
  columnDropCellBorder: true,
  dragHandleColor: 'var(--ag-grid-drag-handle-color)',
  //columnDragIndicatorWidth: 'var(--ag-grid-column-drag-indicator-width)',
  dragAndDropImageBorder: true,
  //dragAndDropImageNotAllowedBorder: true,

  // ============================================
  // FIND & SEARCH
  // ============================================
  findMatchColor: 'var(--ag-grid-find-match-color)',
  findMatchBackgroundColor: 'var(--ag-grid-find-match-background-color)',
  findActiveMatchColor: 'var(--ag-grid-find-active-match-color)',
  findActiveMatchBackgroundColor: 'var(--ag-grid-find-active-match-background-color)',

  // ============================================
  // MISC
  // ============================================
  columnSelectIndentSize: 'var(--ag-grid-column-select-indent-size)',
  setFilterIndentSize: 'var(--ag-grid-set-filter-indent-size)',
  filterToolPanelGroupIndent: 'var(--ag-grid-filter-tool-panel-group-indent)',
  chartMenuLabelColor: 'var(--ag-grid-chart-menu-label-color)',
  chartMenuPanelWidth: 'var(--ag-grid-chart-menu-panel-width)',
  widgetContainerHorizontalPadding: 'var(--ag-grid-widget-container-horizontal-padding)',
  widgetVerticalSpacing: 'var(--ag-grid-widget-vertical-spacing)',
  paginationPanelHeight: 'var(--ag-grid-pagination-panel-height)',
  selectCellBackgroundColor: 'var(--ag-grid-select-cell-background-color)',
  //fullRowEditInvalidBackgroundColor: 'var(--ag-grid-full-row-edit-invalid-background-color)',
  dialogBorder: true,
  tooltipBorder: true,
  //tooltipErrorBorder: true,
  popupShadow: 'var(--ag-grid-popup-shadow)',
  //filterPanelCardSubtleColor: 'var(--ag-grid-filter-panel-card-subtle-color)',
  //filterPanelCardSubtleHoverColor: 'var(--ag-grid-filter-panel-card-subtle-hover-color)',
  //colorPickerColorBorderRadius: 'var(--ag-grid-color-picker-color-border-radius)',
  //colorPickerThumbBorderWidth: 'var(--ag-grid-color-picker-thumb-border-width)',
  //colorPickerThumbSize: 'var(--ag-grid-color-picker-thumb-size)',
  //colorPickerTrackBorderRadius: 'var(--ag-grid-color-picker-track-border-radius)',
  //colorPickerTrackSize: 'var(--ag-grid-color-picker-track-size)',
  pinnedRowFontWeight: 'var(--ag-grid-pinned-row-font-weight)',
  pinnedSourceRowFontWeight: 'var(--ag-grid-pinned-source-row-font-weight)',
  //statusBarLabelFontWeight
  //statusBarValueFontWeight

  // ============================================
  // FORMULA TOKENS
  // ============================================
  //formulaToken1BackgroundColor: 'var(--ag-grid-formula-token-1-background-color)',
  //formulaToken1Border: true,  //formulaToken1Color: 'var(--ag-grid-formula-token-1-color)',
  //formulaToken2BackgroundColor: 'var(--ag-grid-formula-token-2-background-color)',
  //formulaToken2Border: true,
  //formulaToken2Color: 'var(--ag-grid-formula-token-2-color)',
  //formulaToken3BackgroundColor: 'var(--ag-grid-formula-token-3-background-color)',
  //formulaToken3Border: true,
  //formulaToken3Color: 'var(--ag-grid-formula-token-3-color)',
  //formulaToken4BackgroundColor: 'var(--ag-grid-formula-token-4-background-color)',
  //formulaToken4Border: true,
  //formulaToken4Color: 'var(--ag-grid-formula-token-4-color)',
  //formulaToken5BackgroundColor: 'var(--ag-grid-formula-token-5-background-color)',
  //formulaToken5Border: true,
  //formulaToken5Color: 'var(--ag-grid-formula-token-5-color)',
  //formulaToken6BackgroundColor: 'var(--ag-grid-formula-token-6-background-color)',
  //formulaToken6Border: true,
  //formulaToken6Color: 'var(--ag-grid-formula-token-6-color)',
  //formulaToken7BackgroundColor: 'var(--ag-grid-formula-token-7-background-color)',
  //formulaToken7Border: true,
  //formulaToken7Color: 'var(--ag-grid-formula-token-7-color)',
};

console.debug('Theme params:', sharedParams);

/**
 * Mojave theme for AG Grid (Dark Mode)
 * 
 * Uses the same semantic tokens but relies on CSS custom property overrides
 * in ag-grid_semantic.css for dark mode values
 */
export const agGridMojaveLight = themeQuartz
  .withPart(iconSetQuartzLight)
  .withParams({
    ...sharedParams,
    browserColorScheme: 'light',
  });

/**
 * Mojave theme for AG Grid (Dark Mode)
 */
export const agGridMojaveDark = themeQuartz
  .withPart(iconSetQuartzLight)
  .withParams({
    ...sharedParams,
    browserColorScheme: 'dark',
  });